/**
 * Duplicate of ChangeHistoryTimelineModal for CandidateHistoryModal only.
 */
import * as React from 'react';
import { useContext } from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { GenericDialog, GenericDialogContext } from 'app/components/Alerts/GenericDialog';
import Timeline, { type TimelineEvent } from 'app/components/Common/ChangeHistoryTimeline/Timeline';
import {
  ChangeHistoryFilterPanel,
  applyChangeHistoryFilters,
  sortDataByGroup,
  DEFAULT_FILTERS,
  type GroupByOption,
} from 'app/components/Common/ChangeHistoryTimeline/ChangeHistoryFilterPanel';
import { ChangeHistoryInsightsSection } from 'app/components/Common/ChangeHistoryTimeline/ChangeHistoryInsightsSection';

export interface CandidateHistoryModalChangeHistoryRow {
  id?: string;
  fieldChanged?: string;
  oldValue?: string;
  newValue?: string;
  userName?: string;
  created?: string;
  externalSystem?: string;
  [key: string]: unknown;
}

export interface CandidateHistoryModalChangeHistoryTimelineModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  historyData: CandidateHistoryModalChangeHistoryRow[];
}

function mapToTimelineEvent(row: CandidateHistoryModalChangeHistoryRow): TimelineEvent {
  return {
    userId: (row.userId ?? row.id) as string | undefined,
    userName: String(row.userName ?? '—'),
    created: String(row.created ?? ''),
    entity: undefined,
    entityId: undefined,
    entityGuid: null,
    fieldChanged: String(row.fieldChanged ?? '—'),
    oldValue: String(row.oldValue ?? '—'),
    newValue: String(row.newValue ?? '—'),
    externalSystem: String(row.externalSystem ?? '—'),
  };
}

export const CandidateHistoryModalChangeHistoryTimelineModal: React.FC<
  CandidateHistoryModalChangeHistoryTimelineModalProps
> = ({ open, onClose, title, historyData }) => {
  const { t } = useTranslation();
  const { fullscreen } = useContext(GenericDialogContext);
  const [tabValue, setTabValue] = React.useState(0);
  const [filters, setFilters] = React.useState(DEFAULT_FILTERS);
  const [groupBy, setGroupBy] = React.useState<GroupByOption>('none');

  const allRows = React.useMemo(() => (Array.isArray(historyData) ? historyData : []), [historyData]);
  const filteredRows = React.useMemo(() => applyChangeHistoryFilters(allRows, filters), [allRows, filters]);
  const sortedForTimeline = React.useMemo(() => sortDataByGroup(filteredRows, groupBy), [filteredRows, groupBy]);
  const timelineData = React.useMemo(() => sortedForTimeline.map(mapToTimelineEvent), [sortedForTimeline]);

  React.useEffect(() => {
    if (!open) {
      setTabValue(0);
      setFilters(DEFAULT_FILTERS);
      setGroupBy('none');
    }
  }, [open]);

  return (
    <GenericDialog
      open={open}
      onClose={onClose}
      variant="blue"
      dialogTitleProps={{ text: title, closeButton: true, expandable: false }}
      PaperProps={
        fullscreen ? { sx: { maxHeight: '100vh', height: '100vh', overflow: 'hidden' } } : undefined
      }
      dialogContentProps={{
        sx: {
          flex: 1,
          minHeight: 0,
          maxHeight: 'calc(100vh - 80px)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          py: 0,
          px: 0,
          bgcolor: '#fff',
          width: '100%',
        },
      }}
      maxWidth="lg"
      fullWidth
    >
      <Tabs
        value={tabValue}
        onChange={(_, v) => setTabValue(v)}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          px: 2,
          minHeight: 48,
          '& .MuiTab-root': { minHeight: 48, textTransform: 'none', fontWeight: 600 },
        }}
      >
        <Tab label={t('changeHistoryToolbar.tabSummary')} />
        <Tab label={t('changeHistoryToolbar.tabTimeline')} />
      </Tabs>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          ...(fullscreen && { maxHeight: 'calc(100vh - 120px)', height: 'calc(100vh - 120px)' }),
        }}
      >
        {tabValue === 0 && (
          <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
            {filteredRows.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                No changes match the current filters.
              </Typography>
            ) : (
              <ChangeHistoryInsightsSection rows={filteredRows} />
            )}
          </Box>
        )}
        {tabValue === 1 && (
          <>
            <ChangeHistoryFilterPanel
              allRows={allRows}
              displayedRows={filteredRows}
              filters={filters}
              onFiltersChange={setFilters}
              groupBy={groupBy}
              onGroupByChange={setGroupBy}
            />
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
                px: 3,
              }}
            >
              {filteredRows.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center', color: '#777' }}>
                  <Typography variant="body2">No changes match the current filters.</Typography>
                </Box>
              ) : (
                <Timeline data={timelineData} embedded />
              )}
            </Box>
          </>
        )}
      </Box>
    </GenericDialog>
  );
};
